import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { BaseView, BaseText } from '@/components/base'
import { StickyHeaderNavigation } from '@/components/StickyHeaderNavigation'
import { SearchInput } from '@/components/SearchInput'
import { BlockedUserItem } from '@/components/BlockedUserItem'
import { SkeletonListTemplate } from '@/components/SkeletonListTemplate'
import { useGetBlockedUserList, UserBySearchDTO } from '@/domain/identity'
import useOffsets from '@/hooks/useOffsets'
import { normalizeText } from '@/utils/textUtils'

export const BlockedUsersScreen: React.FC = () => {
  const { t } = useTranslation()
  const offsets = useOffsets()
  const [searchValue, setSearchValue] = useState('')

  const { data, refetch, isRefetching, isPending } = useGetBlockedUserList()

  // Filter users by search
  const filteredUsers = useMemo(() => {
    if (!data?.users?.length) return []
    if (!searchValue.trim()) return data.users

    const normalizedSearch = normalizeText(searchValue)
    return data.users.filter(
      ({ first_name, username }) =>
        normalizeText(first_name).includes(normalizedSearch) ||
        normalizeText(username).includes(normalizedSearch),
    )
  }, [data?.users, searchValue])

  const handleSearch = (value: string) => {
    setSearchValue(value)
  }

  const handleClearSearch = () => {
    setSearchValue('')
  }

  const handleRefresh = () => {
    refetch()
  }

  // Loading component
  const LoadingComponent = () => (
    <BaseView mt={20}>
      <SkeletonListTemplate
        sizeList={8}
        itemHeight={72}
        itemRadius={34}
        paddingList={0}
        itemWidth="100%"
        showItemBackground={true}
        itemMarginBottom={true}
        containerGap={6}
      />
    </BaseView>
  )

  // Empty state component
  const EmptyComponent = () => (
    <BaseView
      className="items-center justify-center"
      style={{ marginTop: 200 }}
    >
      <BaseText
        fontSize={16}
        fontFamily="medium"
        color="label.secondary"
        className="text-center"
      >
        {t('translation:blockedUsers.sorryCouldNotFindAnyUsers')}
      </BaseText>
    </BaseView>
  )

  return (
    <BaseView flex={1} bgColor="background.screen" className="h-full">
      {/* Fixed Header Navigation */}
      <StickyHeaderNavigation
        title={t('screens:security.items.blockedUsers')}
      />

      {/* Scrollable Content */}
      <BaseView
        flex={1}
        px={12}
        style={{
          overflowY: 'auto',
          // paddingTop: offsets.top,
          paddingBottom: '40px',
        }}
      >
        {/* Search Input - show only if there are users */}
        {!isPending && !!data?.users && data.users.length >= 1 && (
          <BaseView mt={16}>
            <SearchInput
              value={searchValue}
              onChangeText={handleSearch}
              onClear={handleClearSearch}
              placeholder={t('form:placeholders.search')}
              withoutBorder
            />
          </BaseView>
        )}

        {/* Content */}
        <BaseView mt={20}>
          {isPending ? (
            <LoadingComponent />
          ) : filteredUsers.length > 0 ? (
            <BaseView gap={12}>
              {filteredUsers.map((user) => (
                <BlockedUserItem key={user.id} user={user} />
              ))}
            </BaseView>
          ) : (
            <EmptyComponent />
          )}
        </BaseView>

        {/* Pull to refresh indicator */}
        {isRefetching && !isPending && (
          <BaseView className="absolute top-0 left-0 right-0 items-center pt-4">
            <BaseView
              className="bg-black bg-opacity-20 rounded-full px-3 py-1"
              style={{ backdropFilter: 'blur(10px)' }}
            >
              <BaseText fontSize={12} color="label.default">
                {'Refreshing...'}
              </BaseText>
            </BaseView>
          </BaseView>
        )}
      </BaseView>
    </BaseView>
  )
}

export default BlockedUsersScreen
